<template>
  <div id="app" class="w-100 h-100">
    <b-navbar
      v-if="!bare"
      id="navbar"
      toggleable="lg"
      :type="navText"
      :variant="navColour"
    >
      <b-navbar-brand :href="publicUrl"
        ><img :src="'/static/' + logoName + '.svg'" height="34" />
        <b-icon
          animation="cylon-vertical"
          class="ml-2"
          v-if="isTest"
          icon="exclamation-circle-fill"
          variant="warning"
          font-scale="1.5"
        ></b-icon>
      </b-navbar-brand>
      <b-navbar-toggle target="nav-collapse"></b-navbar-toggle>
      <!-- No is-nav: it reopens the menu on any window resize, e.g. a phone's address bar moving -->
      <b-collapse id="nav-collapse" class="navbar-collapse" v-model="menuOpen">
        <b-navbar-nav class="ml-auto d-none d-lg-flex">
          <b-nav-item v-if="$auth.user.authenticated" right>
            <b-nav-item-dropdown right>
              <template slot="button-content">
                <v-icon name="user" scale="1.5" class="icon" />
                {{ $auth.user.username }}</template
              >
              <b-dropdown-item @click="$router.push({ path: '/account' })">{{
                $t("account.my")
              }}</b-dropdown-item>
              <b-dropdown-item @click="logout()">{{
                $t("account.logout")
              }}</b-dropdown-item>
            </b-nav-item-dropdown>
          </b-nav-item>
          <b-nav-item v-if="!$auth.user.authenticated && !hideAuthLinks" right>
            <a href="#" class="nav-link" v-b-modal.login>
              <v-icon name="sign-in-alt" scale="1.5" class="icon" />
              {{ $t("account.login") }}
            </a>
          </b-nav-item>
          <b-nav-item v-if="!$auth.user.authenticated && !hideAuthLinks" right>
            <a href="#" class="nav-link" v-b-modal.register>
              <v-icon name="user-plus" scale="1.5" class="icon" />
              {{ $t("account.register") }}
            </a>
          </b-nav-item>
          <b-nav-item right :href="supportUrl">
            <a target="_blank" :href="supportUrl" class="nav-link">
              <v-icon name="question-circle" scale="1.5" class="icon" />
              {{ $t("dashboard.common.support") }}
            </a>
          </b-nav-item>
          <b-nav-item right>
            <b-nav-item-dropdown right>
              <template slot="button-content">
                <v-icon name="globe" scale="1.5" class="icon" />
                {{ languages[this.$i18n.locale] }}</template
              >
              <b-dropdown-item @click="lang('en')">English</b-dropdown-item>
              <b-dropdown-item @click="lang('zh')">中文</b-dropdown-item>
            </b-nav-item-dropdown>
          </b-nav-item>
        </b-navbar-nav>
        <!-- Phones and tablets: team, every page as a two-column grid, then
             account, support and language as a 2x2 grid of chips. -->
        <div class="mobile-menu d-lg-none">
          <template v-if="$auth.user.authenticated">
            <div class="mobile-menu-label">{{ $t("dashboard.headers.activeTeam") }}</div>
            <team-selector
              class="mobile-menu-team"
              @updateTeam="updateTeam"
              @picked="onTeamPicked"
              :resetTeamsTrigger="resetTeamsTrigger"
            />
            <nav class="mobile-menu-grid">
              <router-link
                v-for="link in mobileLinks"
                :key="link.uri"
                :to="link.uri"
                class="mobile-menu-tile"
                @click.native="menuOpen = false"
              >
                <v-icon :name="link.icon" />
                <span>{{ $t(link.key) }}</span>
              </router-link>
            </nav>
          </template>
          <div class="mobile-menu-footer">
            <template v-if="$auth.user.authenticated">
              <router-link to="/account" class="mobile-menu-chip" @click.native="menuOpen = false">
                <v-icon name="user" /> <span>{{ $auth.user.username }}</span>
              </router-link>
              <button type="button" class="mobile-menu-chip" @click="logout()">
                <v-icon name="sign-out-alt" /> {{ $t("account.logout") }}
              </button>
            </template>
            <template v-else-if="!hideAuthLinks">
              <button type="button" class="mobile-menu-chip" v-b-modal.login>
                <v-icon name="sign-in-alt" /> {{ $t("account.login") }}
              </button>
              <button type="button" class="mobile-menu-chip" v-b-modal.register>
                <v-icon name="user-plus" /> {{ $t("account.register") }}
              </button>
            </template>
            <a :href="supportUrl" target="_blank" rel="noopener" class="mobile-menu-chip">
              <v-icon name="question-circle" /> {{ $t("dashboard.common.support") }}
            </a>
            <button type="button" class="mobile-menu-chip" @click="lang(otherLocale)">
              <v-icon name="globe" /> {{ languages[otherLocale] }}
            </button>
          </div>
        </div>
      </b-collapse>
    </b-navbar>
    <b-container fluid class="app-body pl-0 pb-0 pr-0" @click="unDrop()">
      <div class="row m-0 h-100">
        <sidebar
          v-if="!bare"
          :links="visibleLinks"
          :active="sidebarActive"
          :user="$auth.user"
          :resetTeamsTrigger="resetTeamsTrigger"
          @updateTeam="updateTeam"
          @hide="sidebarActive = false"
        ></sidebar>
        <div
          class="col-12 pb-0 pr-0 pl-0 h-100"
          v-bind:class="{
            faded: sidebarActive,
            'col-md-12 col-lg-10': $auth.user.authenticated,
          }"
        >
          <transition name="fade">
            <router-view :activeTeam="activeTeam"></router-view>
          </transition>
        </div>
      </div>
    </b-container>
    <notifications></notifications>
    <!-- Both modals hide Bootstrap's header, so each has its own close button. -->
    <b-modal id="login" title="" hide-header no-fade hide-footer>
      <button type="button" class="auth-close" :aria-label="$t('buttons.close')" @click="$bvModal.hide('login')">×</button>
      <login @resetTeams="$nextTick(() => resetTeamsTrigger++)"></login>
    </b-modal>
    <b-modal id="register" title="" centered hide-header no-fade hide-footer>
      <button type="button" class="auth-close" :aria-label="$t('buttons.close')" @click="$bvModal.hide('register')">×</button>
      <register></register>
    </b-modal>
  </div>
</template>

<script>
import Sidebar from "@/components/Sidebar.vue";
import Login from "@/lib/auth/components/Login.vue";
import Register from "@/lib/auth/components/Register.vue";
import TeamSelector from "@/components/TeamSelector.vue";
import { saveLocale } from "@/lang";

import "vue-awesome/icons/user";
import "vue-awesome/icons/sign-in-alt";
import "vue-awesome/icons/sign-out-alt";
import "vue-awesome/icons/user-plus";
import "vue-awesome/icons/question-circle";
import "vue-awesome/icons/globe";
import "vue-awesome/icons/cogs";
import "vue-awesome/icons/calendar";
import "vue-awesome/icons/table";
import "vue-awesome/icons/map";
import "vue-awesome/icons/film";
import "vue-awesome/icons/code-branch";
import "vue-awesome/icons/users";
import "vue-awesome/icons/shopping-cart";
import "vue-awesome/icons/clipboard-list";
import "vue-awesome/icons/sticky-note";
import "vue-awesome/icons/photo-video";
import "vue-awesome/icons/desktop";

export default {
  name: "App",
  components: {
    TeamSelector,
    Sidebar,
    Register,
    Login,
  },
  computed: {
    // Routes such as the public landing page render without the app navbar and sidebar.
    bare() {
      return this.$route.matched.some((r) => r.meta && r.meta.bare);
    },
    // Routes with meta.hideAuthLinks (the old /home page) hide the navbar's
    // Login and Register links. Kept apart from $auth, which is not reactive,
    // so the template still re-checks sign-in on every render.
    hideAuthLinks() {
      return this.$route.matched.some((r) => r.meta && r.meta.hideAuthLinks);
    },
    // Links for the sidebar and mobile menu. $auth isn't reactive, so reading
    // resetTeamsTrigger re-runs this on log-in, log-out and team changes.
    visibleLinks() {
      this.resetTeamsTrigger;
      return this.$auth.user.admin
        ? this.links
        : this.links.filter((link) => !link.admin);
    },
    // The mobile menu lists admin pages alongside the rest instead of nested.
    mobileLinks() {
      return this.visibleLinks.flatMap((link) => link.children || [link]);
    },
    otherLocale() {
      return this.$i18n.locale === "zh" ? "en" : "zh";
    },
  },
  data: function () {
    return {
      publicUrl: process.env.VUE_APP_PUBLIC_URL,
      supportUrl: process.env.VUE_APP_SUPPORT_URL,
      navColour: process.env.VUE_APP_NAV_COLOUR,
      navText: process.env.VUE_APP_NAV_TEXT,
      logoName: process.env.VUE_APP_LOGO,
      languages: {
        en: "English",
        zh: "中文",
      },
      resetTeamsTrigger: 0,
      menuOpen: false,
      activeTeam: { name: "none selected", id: Number(0) },
      cart: this.$cart.store,
      links: [
        {
          key: "index.dashboard",
          uri: "/dashboard",
          icon: "cogs",
        },
        {
          key: "schedule.map",
          uri: "/scheduling/map",
          icon: "map",
          noadmin: true,
        },
        {
          key: "schedule.ipam",
          uri: "/scheduling/ipam",
          icon: "table",
        },
        {
          key: "index.displaymgt",
          uri: "/displays",
          icon: "cogs",
        },
        {
          key: "index.sources",
          uri: "/sources",
          icon: "code-branch",
        },
        {
          key: "index.campaigns",
          uri: "/campaigns",
          icon: "film",
        },

        {
          key: "index.media",
          uri: "/media",
          icon: "photo-video",
        },
        {
          key: "index.logging",
          uri: "/logging",
          icon: "clipboard-list",
        },
        {
          key: "index.teammgt",
          uri: "/teams",
          // admin: true,
          icon: "users",
        },
        {
          key: "index.admin",
          icon: "cogs",
          admin: true,
          children: [
            {
              key: "index.usermgt",
              uri: "/users",
              icon: "user",
            },
            {
              key: "index.news",
              uri: "/news",
              icon: "sticky-note",
            },
          ],
        },
      ],
      sidebarActive: false,
      dropdownOpen: false,
      langDropdown: false,
      isTest: process.env.VUE_APP_API_ROOT.includes("adverdev"),
      beforeUnload_time: 0,
      gap_time: 0,
      logoutTimer: null,
      timeoutInactivity: process.env.VUE_APP_INACTIVITY_OUT * 60 * 1000,
    };
  },
  mounted() {
    window.addEventListener("beforeunload", this.handleBeforeUnload);
    window.addEventListener("unload", this.handleUnload);
    this.resetTeamsTrigger++;
    this.startLogoutTimer();
    window.addEventListener("mousemove", this.resetLogoutTimer);
    window.addEventListener("keypress", this.resetLogoutTimer);
  },
  beforeDestroy: function () {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    window.removeEventListener("unload", this.handleUnload);
    clearTimeout(this.timeoutInactivity);
    window.removeEventListener("mousemove", this.resetLogoutTimer);
    window.removeEventListener("keypress", this.resetLogoutTimer);
  },
  watch: {
    $route() {
      this.menuOpen = false;
    },
  },
  methods: {
    triggerUpdate() {
      this.resetTeamsTrigger++;
    },
    updateTeam(team) {
      this.resetTeamsTrigger++;
      this.activeTeam = team;
    },
    onTeamPicked() {
      this.menuOpen = false;
      this.$router.push("/dashboard").catch(() => {});
    },
    unDrop() {
      this.dropdownOpen = false;
      this.langDropdown = false;
      this.menuOpen = false;
    },
    lang(locale) {
      saveLocale(locale);
      this.$i18n.locale = locale;
      this.$router.go(0);
    },
    logout: function () {
      this.resetTeamsTrigger++;
      this.dropdownOpen = false;
      this.$cart.store = [];
      this.$auth.logout("/");
    },
    toggleShow() {
      event.target.classList.toggle("show");
    },

    handleBeforeUnload() {
      this.beforeUnload_time = new Date().getTime();
    },

    handleUnload() {
      this.gap_time = new Date().getTime() - this.beforeUnload_time;
      if (this.gap_time <= 5 && this.$auth?.user?.id !== 0) {
        this.logout();
      }
    },

    startLogoutTimer() {
      this.logoutTimer = setTimeout(() => {
        this.logout();
      }, this.timeoutInactivity);
    },

    resetLogoutTimer() {
      if (this.$auth?.user?.id !== 0) {
        clearTimeout(this.logoutTimer);
        this.startLogoutTimer();
      }
    },
  },
};
</script>

<style lang="scss">
// Close button for the login and register modals (see the template).
.auth-close {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  font-size: 26px;
  line-height: 1;
  color: #6c757d;
  cursor: pointer;

  &:hover,
  &:focus {
    background: rgba(0, 0, 0, 0.06);
    color: #212529;
  }
}

@import "node_modules/bootstrap/scss/_functions.scss";
@import "node_modules/bootstrap/scss/_variables.scss";
@import "node_modules/bootstrap/scss/mixins/_breakpoints.scss";

@include media-breakpoint-up(lg) {
  .dropdown-toggle {
    display: inline-block;
  }

  .link-toggle {
    display: inline-block;
  }

  .logo {
    display: inline-block;
  }
}

@include media-breakpoint-down(md) {
  .nav-link {
    padding-right: 0;
    padding-left: 0;
  }
}

@include media-breakpoint-up(xs) {
  .toggle-center {
    align-self: center;
    cursor: pointer;
  }

  .fade-enter-active {
    transition: opacity 0.5s;
  }

  .fade-enter {
    opacity: 0;
  }

  .content {
    -webkit-transition: all 0.3s ease-in-out;
    -moz-transition: all 0.3s ease-in-out;
    -ms-transition: all 0.3s ease-in-out;
    -o-transition: all 0.3s ease-in-out;
    transition: all 0.3s ease-in-out;
  }

  .content.faded {
    background: rgba(0, 0, 0, 0.7);
    -webkit-transition: all 0.3s ease-in-out;
    -moz-transition: all 0.3s ease-in-out;
    -ms-transition: all 0.3s ease-in-out;
    -o-transition: all 0.3s ease-in-out;
    transition: all 0.3s ease-in-out;
  }
}

// The navbar sits in the page flow and the content fills the rest of the
// screen, so no navbar height (logo, zoom, display scaling, the landing page
// having none) can leave content underneath it. It used to be fixed, with
// the gap measured in JS only on load and on resize.
#app {
  display: flex;
  flex-direction: column;
}
#navbar {
  flex: none;
  z-index: 1030; // above the sidebar (1020), as Bootstrap's fixed-top was
}
.app-body {
  flex: 1 1 auto;
  min-height: 0;
}

// The whole open menu scrolls within the screen if it is taller.
@include media-breakpoint-down(md) {
  #nav-collapse {
    max-height: calc(100vh - 4rem);
    max-height: calc(100dvh - 4rem);
    overflow-y: auto;
  }
}

.mobile-menu {
  margin: 0.5rem 0 0.75rem;
  padding: 0.75rem;
  border-radius: 0.75rem;
  background: #fff;
  text-align: left;
}
.mobile-menu-label {
  padding: 0 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6c757d;
}
// TeamSelector brings its own row, column and margins.
.mobile-menu-team .m-2 {
  margin: 0.25rem 0 0.75rem !important;
  padding: 0;
}
.mobile-menu-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
}
.mobile-menu-tile {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  min-height: 44px;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  background: #f3f3f5;
  font-size: 0.875rem;
  line-height: 1.2;

  &,
  &:hover,
  &:focus {
    color: #212529;
    text-decoration: none;
  }
  &:hover,
  &:focus {
    background: #e7e7eb;
  }
  &.router-link-active {
    background: #343a40;
    color: #fff;
  }
  svg {
    flex: none;
  }
}
.mobile-menu-footer {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
}
.mobile-menu-grid + .mobile-menu-footer {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e9ecef;
}
.mobile-menu-chip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  min-height: 36px;
  padding: 0.35rem 0.8rem;
  border: 1px solid #dee2e6;
  border-radius: 999px;
  background: #fff;
  font-size: 0.8125rem;
  cursor: pointer;

  &,
  &:hover,
  &:focus {
    color: #212529;
    text-decoration: none;
  }
  &:hover,
  &:focus {
    background: #f3f3f5;
  }
  svg {
    flex: none;
  }
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.icon {
  color: white;
}

.nav-link {
  &.inline {
    display: inline;
  }
}

a:focus {
  color: darken(white, 20%);
}

.flatpickr-calendar {
  z-index: 1080 !important;
}
</style>
